import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Slot } from '@radix-ui/react-slot'
import {
  expandableListContent,
  expandableListTrigger,
} from '@/components/expandableList/ExpandableList.css'
import clsx from 'clsx'

type ExpandableListProps = {
  limit?: number
}

type ExpandableListContextType = {
  isToggled: boolean
  setIsToggled: (value: boolean) => void
  setChildrenCount: (value: number) => void
  childrenCount: number
  limit: number
  columnsCount: number
  setColumnsCount: (columnsCount: number) => void
}

const ExpandableListContext = React.createContext(
  {} as ExpandableListContextType
)

export const ExpandableList = ({
  limit = 8,
  children,
}: React.PropsWithChildren<ExpandableListProps>) => {
  const [isToggled, setIsToggled] = useState(false)
  const [childrenCount, setChildrenCount] = useState(0)
  const [columnsCount, setColumnsCount] = useState(1)
  // Make sure that when dealing with display grid
  // we don't show a row with empty columns
  const parsedLimit = useMemo(() => {
    if (limit % columnsCount === 0) return limit
    return Math.ceil(limit / columnsCount) * columnsCount
  }, [limit, columnsCount])
  return (
    <ExpandableListContext.Provider
      value={{
        limit: parsedLimit,
        childrenCount,
        setChildrenCount,
        isToggled,
        setIsToggled,
        columnsCount,
        setColumnsCount,
      }}
    >
      {children}
    </ExpandableListContext.Provider>
  )
}

type ExpandableListContentProps = {
  as?: React.ElementType
  className?: string
  style?: React.CSSProperties
}
const ExpandableListContent = ({
  children,
  as = 'div',
  className = '',
  style = {},
}: React.PropsWithChildren<ExpandableListContentProps>) => {
  const { isToggled, setColumnsCount, setChildrenCount, limit } = useContext(
    ExpandableListContext
  )
  const childrenCount = React.Children.count(children)
  const cardRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    setChildrenCount(childrenCount)
  }, [childrenCount, setChildrenCount])
  const filteredChildren = React.Children.map(children, (child, index) => {
    if (!isToggled && index >= limit) return null
    return child
  })

  const calculateTheNumberOfColumns = useCallback(() => {
    if (!cardRef.current) return
    const templateColumns = getComputedStyle(cardRef.current)[
      'grid-template-columns'
    ]
    if (!templateColumns) {
      setColumnsCount(1)
    } else {
      const columnsArray = templateColumns.split(' ')
      setColumnsCount(columnsArray.length)
    }
  }, [setColumnsCount])

  useEffect(() => {
    calculateTheNumberOfColumns()
  }, [calculateTheNumberOfColumns])

  useEffect(() => {
    // validate window exists
    if (typeof window === 'undefined') return
    window.addEventListener('resize', calculateTheNumberOfColumns)
    return () =>
      window.removeEventListener('resize', calculateTheNumberOfColumns)
  }, [calculateTheNumberOfColumns])
  const Component = as
  return (
    <Component
      style={style}
      ref={cardRef}
      className={clsx(expandableListContent, className)}
    >
      {filteredChildren}
    </Component>
  )
}

const ExpandableListTrigger = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const { isToggled, setIsToggled, childrenCount, limit } = useContext(
    ExpandableListContext
  )

  const toggle = useCallback(() => {
    setIsToggled(!isToggled)
  }, [isToggled, setIsToggled])
  if (childrenCount <= limit) return null

  if (!children) {
    return (
      <div className={expandableListTrigger} onClick={toggle}>
        {isToggled ? 'see less...' : 'see more...'}
      </div>
    )
  }
  return (
    <Slot onClick={toggle} data-is-toggled={isToggled}>
      {children}
    </Slot>
  )
}

ExpandableList.Trigger = ExpandableListTrigger
ExpandableList.Content = ExpandableListContent
